// =============================================================================
// ui/tabs/MergeAccountsModal.tsx — Review & Merge duplicate accounts.
// -----------------------------------------------------------------------------
// PORTED FROM `app/index.html:46490` (`openMergeAccountsModal`) — #73.
//
// ── 🚨 THE KEEPER IS A CHOICE, AND THE COUNTS MOVE WITH IT ──
//
// Which copy survives decides which firm, rules, drawdown and commission
// schedule the merged account has — so the picker is the control, and the
// preview under it recomputes on every change. A default is offered (the copy
// with a firm set, then the one with the most trades) because that is almost
// always the real account, but it is a default and nothing more.
//
// ── 🚨 WHAT THIS SCREEN PROMISES THAT THE OLD ONE DID NOT ──
//
// Beta's merge deletes the losing account, and `account_adjustments` cascades on
// that delete — so its eval fees, activation fees, resets and payouts go with
// it, silently, and beta's copy mentions only trades. Here they MOVE onto the
// keeper, and the preview says how many. See `io/sync/mergeAccounts.ts`.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSupabase } from '../state/supabase';
import { mergeAccounts } from '../../io/sync/mergeAccounts';
import { deleteTrades } from '../../io/sync/bulkEdit';
import { countDeleteImpact, deleteAccount } from '../../io/sync/deleteAccount';
import {
  defaultMergeKeeper, planAccountMerge, tradeCountsByAccount,
  type MergeAccount,
} from '../../selectors/accountMerge';
import type { DupSigTrade } from '../../selectors/duplicateTrades';
import { num } from '../format';

export interface MergeAccountsModalProps {
  /** One group of same-named accounts. */
  group: readonly MergeAccount[];
  /** Every trade's signature fields — the same read the banner used. */
  rows: readonly DupSigTrade[];
  onClose: () => void;
  onMerged: () => void;
}

export function MergeAccountsModal(
  { group, rows, onClose, onMerged }: MergeAccountsModalProps,
) {
  const { sb, userId } = useSupabase();
  const counts = useMemo(() => tradeCountsByAccount(rows), [rows]);
  const countOf = useCallback(
    (id: string) => counts.get(String(id)) ?? 0, [counts],
  );

  const [keepId, setKeepId] = useState<string>(
    () => String(defaultMergeKeeper(group, countOf)?.id ?? group[0]?.id ?? ''),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dupeIds = useMemo(
    () => group.map((a) => String(a.id)).filter((id) => id !== keepId),
    [group, keepId],
  );

  const plan = useMemo(
    () => planAccountMerge({ keepId, dupeIds, trades: rows }),
    [keepId, dupeIds, rows],
  );

  const run = useCallback(async () => {
    setBusy(true);
    setError(null);
    const res = await mergeAccounts({
      sb,
      userId,
      keepId,
      dupeIds,
      repointIds: plan.repointIds,
      removeIds: plan.removeIds,
      deleteTrades,
      countDeleteImpact,
      deleteAccount,
    });
    setBusy(false);
    if (!res.ok) { setError(res.error ?? 'The merge did not finish.'); return; }
    onMerged();
    onClose();
  }, [sb, userId, keepId, dupeIds, plan, onMerged, onClose]);

  const keeper = group.find((a) => String(a.id) === keepId);

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true"
      aria-label="Merge duplicate accounts">
      <div className="modal">
        <header className="modal-head">
          <h3>Merge duplicate accounts</h3>
          <button type="button" className="btn" onClick={onClose}
            aria-label="Close">×</button>
        </header>

        <div className="modal-body">
          <p className="muted">
            These accounts share a name — usually one account imported from two
            platforms. Pick the copy to <b>keep</b>: the others’ trades move onto
            it, and any trade it already has in identical form is removed rather
            than duplicated.
          </p>

          <label className="mg-keep">
            <span>Keep</span>
            <select
              value={keepId}
              onChange={(e) => setKeepId(e.target.value)}
              aria-label="Account to keep"
            >
              {group.map((a) => (
                <option key={String(a.id)} value={String(a.id)}>
                  {a.name} · {String(a.firm ?? '').trim() || 'no firm'} ·{' '}
                  {num(countOf(String(a.id)), 0)} trades
                </option>
              ))}
            </select>
          </label>

          {/* ⚠ THE PREVIEW IS THE CONSENT. It recomputes on every keeper change,
              so the numbers on screen are always the numbers the button will
              act on. */}
          <div className="mg-preview" data-testid="merge-preview">
            {/* ⚠ "1 trade moves", "2 trades move" — the verb agrees too, and the
                first version of this line said "1 trade move onto". Caught by
                the test rather than by reading it, which is the usual way. */}
            <b>{num(plan.repointIds.length, 0)}</b>{' '}
            {plan.repointIds.length === 1 ? 'trade moves' : 'trades move'} onto{' '}
            <b>{keeper?.name ?? 'the keeper'}</b> ·{' '}
            <b>{num(plan.removeIds.length, 0)}</b>{' '}
            duplicate{plan.removeIds.length === 1 ? '' : 's'} removed ·{' '}
            <b>{num(dupeIds.length, 0)}</b>{' '}
            account{dupeIds.length === 1 ? '' : 's'} deleted
          </div>

          {/* 🚨 THE SENTENCE BETA'S MODAL DOES NOT HAVE. Its merge destroys the
              losing account's fee records through the cascade and says nothing;
              here they move, and the reader is told so before pressing. */}
          <p className="muted mg-note">
            Eval fees, resets, activation fees and payouts recorded against the
            other {dupeIds.length === 1 ? 'copy' : 'copies'} move onto the keeper
            too, so your spend total does not change.
          </p>

          <p className="muted mg-note">
            ⚠ This cannot be undone from here. The trades and fee records are not
            deleted — they move — but the other{' '}
            {dupeIds.length === 1 ? 'account is' : 'accounts are'} removed.
          </p>

          {error ? <div className="cmp-error" role="alert">{error}</div> : null}
        </div>

        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button" className="btn primary"
            disabled={busy || !dupeIds.length}
            onClick={() => { void run(); }}
          >
            {busy ? 'Merging…' : `Merge ${num(dupeIds.length + 1, 0)} into one`}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
